import React from "react";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "../ui/Collapsible";
import Button from "../ui/Button";
import Separator from "../ui/Separator";
import Tooltip from "../ui/Tooltip";
import {
  Activity,
  BarChart3,
  Bug,
  Camera,
  Eye,
  GitBranch,
  Grid,
  Grid3X3,
  History,
  Link2,
  Lightbulb,
  ListTree,
  Mountain,
  Network,
  Package,
  Palette,
  Play,
  Radio,
  Ruler,
  ShieldCheck,
  Upload,
  Weight,
  Zap,
} from "lucide-react";
import { SceneBuilderIcon } from "../ui/Icons";
import {
  MAIN_MENU_CONTROL_SURFACE_STYLE,
  MAIN_MENU_DRAGGABLE_PANEL_TOGGLE_CLASS,
  MAIN_MENU_PANEL_TOGGLE_OPEN_CLASS,
  PANEL_SQUARE_TOGGLE_BUTTON_CLASS,
  PANEL_SQUARE_TOGGLE_ICON_CLASS,
  PANEL_SQUARE_TOGGLE_ICON_SLOT_CLASS,
  PANEL_SQUARE_TOGGLE_LABEL_CLASS,
  PANEL_SQUARE_TOGGLE_OUTLINE_STYLE,
  PANEL_SQUARE_TOGGLE_SIZE_CLASS,
} from "./constants";

const PanelToggleButton = React.memo(function PanelToggleButton({
  isOpen,
  setOpen,
  Icon,
  label,
  className,
  /** When false, do not show the open-state eye indicator (used where it reads like a visibility control). */
  showOpenEye = true,
  variant = "ghost",
  style,
  disabled = false,
  disabledTooltip,
}) {
  const openBlocked = disabled && !isOpen;
  const handleClick = React.useCallback(() => {
    if (openBlocked) return;
    setOpen(!isOpen);
  }, [openBlocked, setOpen, isOpen]);

  const mergedStyle = { ...MAIN_MENU_CONTROL_SURFACE_STYLE, ...style };

  const mergedClassName = [MAIN_MENU_DRAGGABLE_PANEL_TOGGLE_CLASS, className]
    .filter(Boolean)
    .join(" ");

  const button = (
    <Button
      variant={variant}
      size="small"
      className={mergedClassName}
      style={mergedStyle}
      onClick={handleClick}
      disabled={openBlocked}
      aria-label={label}
    >
      <Icon className="w-4 h-4" />
      <span className="flex-1 text-left">{label}</span>
      <div className="w-4 h-4 flex items-center justify-center">
        {showOpenEye && isOpen && <Eye className="w-4 h-4" />}
      </div>
    </Button>
  );

  return openBlocked ? (
    <Tooltip content={disabledTooltip} fullWidth>
      {button}
    </Tooltip>
  ) : (
    button
  );
});

const SceneSquareToggleButton = React.memo(function SceneSquareToggleButton({
  isOpen,
  setOpen,
  Icon,
  label,
  className = PANEL_SQUARE_TOGGLE_SIZE_CLASS,
  labelClassName = "",
  disabled = false,
  disabledTooltip,
}) {
  const openBlocked = disabled && !isOpen;
  const handleClick = React.useCallback(() => {
    if (openBlocked) return;
    setOpen(!isOpen);
  }, [openBlocked, setOpen, isOpen]);

  const button = (
    <button
      type="button"
      className={`${PANEL_SQUARE_TOGGLE_BUTTON_CLASS} ${isOpen ? MAIN_MENU_PANEL_TOGGLE_OPEN_CLASS : ""} ${openBlocked ? "cursor-not-allowed opacity-50 hover:bg-transparent" : ""} ${className}`}
      style={PANEL_SQUARE_TOGGLE_OUTLINE_STYLE}
      onClick={handleClick}
      disabled={openBlocked}
      aria-label={label}
    >
      <span className={PANEL_SQUARE_TOGGLE_ICON_SLOT_CLASS}>
        <Icon className={PANEL_SQUARE_TOGGLE_ICON_CLASS} />
      </span>
      <span className={`${PANEL_SQUARE_TOGGLE_LABEL_CLASS} ${labelClassName}`}>{label}</span>
      {isOpen && <Eye className="main-menu-square-toggle-eye absolute right-1 top-1 h-3 w-3" />}
    </button>
  );

  return openBlocked ? (
    <Tooltip content={disabledTooltip} fullWidth>
      {button}
    </Tooltip>
  ) : (
    button
  );
});

const SceneSection = React.memo(function SceneSection({
  sceneInventoryOpen,
  setSceneInventoryOpen,
  sceneGraphOpen,
  setSceneGraphOpen,
}) {
  return (
    <>
      <Collapsible defaultOpen>
        <CollapsibleTrigger className="select-none text-gray-200">
          Scene Structure
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="grid w-full grid-cols-2 gap-3 pt-2">
            <SceneSquareToggleButton
              isOpen={sceneInventoryOpen}
              setOpen={setSceneInventoryOpen}
              Icon={ListTree}
              label="Scene Inventory"
            />
            <SceneSquareToggleButton
              isOpen={sceneGraphOpen}
              setOpen={setSceneGraphOpen}
              Icon={Network}
              label="Scene Graph"
            />
          </div>
        </CollapsibleContent>
      </Collapsible>
      <Separator />
    </>
  );
});

const RobotModelSections = React.memo(function RobotModelSections({
  isMobilePanelViewport,
  objectLibraryOpen,
  setObjectLibraryOpen,
  actuatorControlsOpen,
  setActuatorControlsOpen,
  materialEditorPanelOpen,
  setMaterialEditorPanelOpen,
  lightsPanelOpen,
  setLightsPanelOpen,
  cameraProbeOpen,
  setCameraProbeOpen,
  modelCheckOpen,
  setModelCheckOpen,
  massPropertiesOpen,
  setMassPropertiesOpen,
  sensorsOpen,
  setSensorsOpen,
  terrainPanelOpen,
  setTerrainPanelOpen,
  getPanelToggleButtonClass,
  showDevTools,
  authoringLocked = false,
  authoringLockedTooltip,
}) {
  return (
    <>
      <Collapsible defaultOpen>
        <CollapsibleTrigger className="select-none text-gray-200">
          Robot Hardware
        </CollapsibleTrigger>
        <CollapsibleContent>
          {isMobilePanelViewport ? (
            <div className="grid w-full grid-cols-2 gap-3 pt-2">
              <SceneSquareToggleButton
                isOpen={objectLibraryOpen}
                setOpen={setObjectLibraryOpen}
                Icon={SceneBuilderIcon}
                label="Library"
                disabled={authoringLocked}
                disabledTooltip={authoringLockedTooltip}
              />
              <SceneSquareToggleButton
                isOpen={actuatorControlsOpen}
                setOpen={setActuatorControlsOpen}
                Icon={Zap}
                label="Joints & Actuators"
              />
              <SceneSquareToggleButton
                isOpen={sensorsOpen}
                setOpen={setSensorsOpen}
                Icon={Activity}
                label="Sensors"
              />
              <SceneSquareToggleButton
                isOpen={cameraProbeOpen}
                setOpen={setCameraProbeOpen}
                Icon={Camera}
                label="Camera Probe"
              />
            </div>
          ) : (
            <div className="space-y-2 pt-2">
              <PanelToggleButton
                isOpen={objectLibraryOpen}
                setOpen={setObjectLibraryOpen}
                Icon={SceneBuilderIcon}
                label="Library"
                className={getPanelToggleButtonClass(objectLibraryOpen)}
                disabled={authoringLocked}
                disabledTooltip={authoringLockedTooltip}
              />
              <PanelToggleButton
                isOpen={actuatorControlsOpen}
                setOpen={setActuatorControlsOpen}
                Icon={Zap}
                label="Joints & Actuators"
                className={getPanelToggleButtonClass(actuatorControlsOpen)}
              />
              <PanelToggleButton
                isOpen={sensorsOpen}
                setOpen={setSensorsOpen}
                Icon={Activity}
                label="Sensors"
                className={getPanelToggleButtonClass(sensorsOpen)}
              />
              <PanelToggleButton
                isOpen={cameraProbeOpen}
                setOpen={setCameraProbeOpen}
                Icon={Camera}
                label="Camera Probe"
                className={getPanelToggleButtonClass(cameraProbeOpen)}
              />
            </div>
          )}
        </CollapsibleContent>
      </Collapsible>
      <Separator />
      <Collapsible defaultOpen>
        <CollapsibleTrigger className="select-none text-gray-200">
          Environment & Appearance
        </CollapsibleTrigger>
        <CollapsibleContent>
          {isMobilePanelViewport ? (
            <div className="grid w-full grid-cols-2 gap-3 pt-2">
              <SceneSquareToggleButton
                isOpen={terrainPanelOpen}
                setOpen={setTerrainPanelOpen}
                Icon={Mountain}
                label="Terrain"
                disabled={authoringLocked}
                disabledTooltip={authoringLockedTooltip}
              />
              <SceneSquareToggleButton
                isOpen={materialEditorPanelOpen}
                setOpen={setMaterialEditorPanelOpen}
                Icon={Palette}
                label="Material Editor"
                disabled={authoringLocked}
                disabledTooltip={authoringLockedTooltip}
              />
              <SceneSquareToggleButton
                isOpen={lightsPanelOpen}
                setOpen={setLightsPanelOpen}
                Icon={Lightbulb}
                label="Lights"
                disabled={authoringLocked}
                disabledTooltip={authoringLockedTooltip}
              />
            </div>
          ) : (
            <div className="space-y-2 pt-2">
              <PanelToggleButton
                isOpen={terrainPanelOpen}
                setOpen={setTerrainPanelOpen}
                Icon={Mountain}
                label="Terrain"
                className={getPanelToggleButtonClass(terrainPanelOpen)}
                disabled={authoringLocked}
                disabledTooltip={authoringLockedTooltip}
              />
              <PanelToggleButton
                isOpen={materialEditorPanelOpen}
                setOpen={setMaterialEditorPanelOpen}
                Icon={Palette}
                label="Material Editor"
                className={getPanelToggleButtonClass(materialEditorPanelOpen)}
                disabled={authoringLocked}
                disabledTooltip={authoringLockedTooltip}
              />
              <PanelToggleButton
                isOpen={lightsPanelOpen}
                setOpen={setLightsPanelOpen}
                Icon={Lightbulb}
                label="Lights"
                className={getPanelToggleButtonClass(lightsPanelOpen)}
                disabled={authoringLocked}
                disabledTooltip={authoringLockedTooltip}
              />
            </div>
          )}
        </CollapsibleContent>
      </Collapsible>
      <Separator />
      <Collapsible defaultOpen>
        <CollapsibleTrigger className="select-none text-gray-200">Analysis</CollapsibleTrigger>
        <CollapsibleContent>
          {isMobilePanelViewport ? (
            <div className="grid w-full grid-cols-2 gap-3 pt-2">
              <SceneSquareToggleButton
                isOpen={modelCheckOpen}
                setOpen={setModelCheckOpen}
                Icon={ShieldCheck}
                label="Physics Readiness"
              />
              <SceneSquareToggleButton
                isOpen={massPropertiesOpen}
                setOpen={setMassPropertiesOpen}
                Icon={Weight}
                label="Mass & Balance"
              />
            </div>
          ) : (
            <div className="space-y-2 pt-2">
              <PanelToggleButton
                isOpen={modelCheckOpen}
                setOpen={setModelCheckOpen}
                Icon={ShieldCheck}
                label="Physics Readiness"
                className={getPanelToggleButtonClass(modelCheckOpen)}
              />
              <PanelToggleButton
                isOpen={massPropertiesOpen}
                setOpen={setMassPropertiesOpen}
                Icon={Weight}
                label="Mass & Balance"
                className={getPanelToggleButtonClass(massPropertiesOpen)}
              />
            </div>
          )}
        </CollapsibleContent>
      </Collapsible>
      {showDevTools ? <Separator /> : null}
    </>
  );
});

const TrainingIkSection = React.memo(function TrainingIkSection({
  visible,
  showLeadingSeparator,
  warmstartDatasetOpen,
  setWarmstartDatasetOpen,
  behaviorEditorOpen,
  setBehaviorEditorOpen,
  octoTrainingOpen,
  setOctoTrainingOpen,
  trainingPipelineOpen,
  setTrainingPipelineOpen,
  trainingMetricsOpen,
  setTrainingMetricsOpen,
  policyRolloutOpen,
  setPolicyRolloutOpen,
  ikTargetPanelOpen,
  setIkTargetPanelOpen,
  getPanelToggleButtonClass,
}) {
  if (!visible) return null;
  return (
    <>
      {showLeadingSeparator ? <Separator /> : null}
      <Collapsible defaultOpen>
        <CollapsibleTrigger className="select-none text-gray-200">Training & IK</CollapsibleTrigger>
        <CollapsibleContent>
          <div className="space-y-2 pt-2">
            <PanelToggleButton
              isOpen={warmstartDatasetOpen}
              setOpen={setWarmstartDatasetOpen}
              Icon={Package}
              label="Warm-start Dataset"
              className={getPanelToggleButtonClass(warmstartDatasetOpen)}
            />
            <PanelToggleButton
              isOpen={behaviorEditorOpen}
              setOpen={setBehaviorEditorOpen}
              Icon={GitBranch}
              label="Behavior editor"
              className={getPanelToggleButtonClass(behaviorEditorOpen)}
            />
            <PanelToggleButton
              isOpen={trainingPipelineOpen}
              setOpen={setTrainingPipelineOpen}
              Icon={Play}
              label="Training Setup"
              className={getPanelToggleButtonClass(trainingPipelineOpen)}
            />
            <PanelToggleButton
              isOpen={octoTrainingOpen}
              setOpen={setOctoTrainingOpen}
              Icon={Play}
              label="Octo Training"
              className={getPanelToggleButtonClass(octoTrainingOpen)}
            />
            <PanelToggleButton
              isOpen={trainingMetricsOpen}
              setOpen={setTrainingMetricsOpen}
              Icon={BarChart3}
              label="Training Metrics"
              className={getPanelToggleButtonClass(trainingMetricsOpen)}
            />
            <PanelToggleButton
              isOpen={policyRolloutOpen}
              setOpen={setPolicyRolloutOpen}
              Icon={Radio}
              label="Policy Rollout"
              className={getPanelToggleButtonClass(policyRolloutOpen)}
            />
            <PanelToggleButton
              isOpen={ikTargetPanelOpen}
              setOpen={setIkTargetPanelOpen}
              Icon={Link2}
              label="IK Target Picker"
              className={getPanelToggleButtonClass(ikTargetPanelOpen)}
            />
          </div>
        </CollapsibleContent>
      </Collapsible>
      <Separator />
    </>
  );
});

const DevToolsSection = React.memo(function DevToolsSection({
  visible,
  showFullDevTools,
  animationPanelOpen,
  setAnimationPanelOpen,
  circleDetectionOpen,
  setCircleDetectionOpen,
  runtimeDebugOpen,
  setRuntimeDebugOpen,
  measurementsOpen,
  setMeasurementsOpen,
  historyPanelOpen,
  setHistoryPanelOpen,
  showPerformancePanel,
  onTogglePerformancePanel,
  filePanelOpen,
  setFilePanelOpen,
  meshEditorOpen,
  setMeshEditorOpen,
  xmlSourcePanelOpen,
  setXmlSourcePanelOpen,
  gremlinsPanelOpen,
  setGremlinsPanelOpen,
  getPanelToggleButtonClass,
}) {
  if (!visible) return null;
  return (
    <Collapsible defaultOpen>
      <CollapsibleTrigger className="select-none text-gray-200">Dev Tools</CollapsibleTrigger>
      <CollapsibleContent>
        <div className="space-y-2 pt-2">
          {showFullDevTools ? (
            <>
              <PanelToggleButton
                isOpen={animationPanelOpen}
                setOpen={setAnimationPanelOpen}
                Icon={Play}
                label="Pose Editor"
                className={getPanelToggleButtonClass(animationPanelOpen)}
              />
              <PanelToggleButton
                isOpen={circleDetectionOpen}
                setOpen={setCircleDetectionOpen}
                Icon={Grid}
                label="Circle Detection"
                className={getPanelToggleButtonClass(circleDetectionOpen)}
              />
            </>
          ) : null}
          <PanelToggleButton
            isOpen={runtimeDebugOpen}
            setOpen={setRuntimeDebugOpen}
            Icon={BarChart3}
            label="Runtime Diagnostics"
            className={getPanelToggleButtonClass(runtimeDebugOpen)}
          />
          {showFullDevTools ? (
            <PanelToggleButton
              isOpen={measurementsOpen}
              setOpen={setMeasurementsOpen}
              Icon={Ruler}
              label="Measurements"
              className={getPanelToggleButtonClass(measurementsOpen)}
            />
          ) : null}
          {showFullDevTools ? (
            <>
              <PanelToggleButton
                isOpen={historyPanelOpen}
                setOpen={setHistoryPanelOpen}
                Icon={History}
                label="History Status"
                className={getPanelToggleButtonClass(historyPanelOpen)}
              />
              <PanelToggleButton
                isOpen={showPerformancePanel}
                setOpen={() => onTogglePerformancePanel()}
                Icon={BarChart3}
                label="Performance Panel"
                className={getPanelToggleButtonClass(showPerformancePanel)}
              />
              <PanelToggleButton
                isOpen={filePanelOpen}
                setOpen={setFilePanelOpen}
                Icon={Upload}
                label="File Upload Viewer"
                className={getPanelToggleButtonClass(filePanelOpen)}
              />
              <PanelToggleButton
                isOpen={meshEditorOpen}
                setOpen={setMeshEditorOpen}
                Icon={Grid3X3}
                label="Batch Mesh Origin Repair"
                className={getPanelToggleButtonClass(meshEditorOpen)}
              />
              <PanelToggleButton
                isOpen={xmlSourcePanelOpen}
                setOpen={setXmlSourcePanelOpen}
                Icon={Upload}
                label="XML Source of Truth"
                className={getPanelToggleButtonClass(xmlSourcePanelOpen)}
              />
              {import.meta.env.DEV && (
                <PanelToggleButton
                  isOpen={gremlinsPanelOpen}
                  setOpen={setGremlinsPanelOpen}
                  Icon={Bug}
                  label="Gremlins"
                  className={getPanelToggleButtonClass(gremlinsPanelOpen)}
                />
              )}
            </>
          ) : null}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
});

export { DevToolsSection, RobotModelSections, SceneSection, TrainingIkSection };
